// src/components/pages/mosque/MosqueMapPage.tsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  MarkerF,
  MarkerClustererF,
  InfoWindowF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "../../../utils/i18nStub";
import { GOOGLE_MAPS_API_KEY } from "../../../config";

import { listMosqueMapPoints } from "../../../services/mosqueService";

// ✅ SAME breadcrumb component used in MosqueSearchPage
import Breadcrumbs from "../../../components/navigation/Breadcrumbs";

// ✅ USE THE EXACT SAME FILTER UI AS MOSQUE SEARCH
import MosqueFilters, {
  MosqueFilterState,
} from "../../../components/pages/mosque/MosqueFilters";

// -----------------------------
// Types
// -----------------------------
type MosqueMapPoint = {
  id: number;
  mosqueName: string;
  latitude: number | null;
  longitude: number | null;
  capacity: number | null;
  hasWomenPrayerRoom?: string | boolean | null;
  mosqueClassification: string;
  distanceM?: number | null;
};

type LatLng = { lat: number; lng: number };

// -----------------------------
// Helpers
// -----------------------------
const DEFAULT_CENTER: LatLng = { lat: 24.7136, lng: 46.6753 }; // Riyadh fallback
const DEFAULT_ZOOM = 11;

const RADIUS_KM_OPTIONS = [1, 2, 3, 5, 10, 20] as const;
type RadiusKm = (typeof RADIUS_KM_OPTIONS)[number];

function kmToMeters(km: number) {
  return Math.round(km * 1000);
}

function buildGoogleMapsPinUrl(dest: LatLng) {
  return `https://www.google.com/maps?q=${encodeURIComponent(
    `${dest.lat},${dest.lng}`
  )}`;
}

function buildGoogleMapsDirectionsUrl(origin: LatLng, dest: LatLng) {
  const o = `${origin.lat},${origin.lng}`;
  const d = `${dest.lat},${dest.lng}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    o
  )}&destination=${encodeURIComponent(d)}&travelmode=driving`;
}

// ✅ Fix LoadScript reload warning (keep array stable)
const GMAP_LIBS: ("places" | "geometry" | "drawing" | "visualization")[] = [];

// -----------------------------
// Component
// -----------------------------
export default function MosqueMapPage() {
  const { t } = useTranslation("mosqueMap");
  const navigate = useNavigate();

  // ✅ i18nStub fallback helper
  const tt = (key: string, fallback: string) => {
    const v = t(key);
    const last = key.includes(".") ? key.split(".").pop() : key;
    if (!v || v === key || v === last) return fallback;
    return v;
  };

  // ✅ load google maps script using your config key
  const { isLoaded, loadError } = useJsApiLoader({
    id: "emasajid-google-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: GMAP_LIBS,
  });

  // -----------------------------
  // Mosque marker icon
  // ✅ FIXED for /home deployment
  // -----------------------------
  const MOSQUE_ICON_URL = `${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosque-location-256.png`;

  const mosqueIcon = useMemo(() => {
    if (!isLoaded || !(window as any).google) return undefined;

    const g = (window as any).google;
    const W = 44;
    const H = 44;

    return {
      url: MOSQUE_ICON_URL,
      scaledSize: new g.maps.Size(W, H),
      anchor: new g.maps.Point(W / 2, H),
    };
  }, [isLoaded]);

  // -----------------------------
  // Women formatter (localized)
  // -----------------------------
  const formatWomen = useCallback(
    (v?: string | boolean | null) => {
      if (v === null || v === undefined) return "—";
      if (typeof v === "boolean")
        return v ? tt("options.yes", "نعم") : tt("options.no", "لا");

      const s = String(v).trim().toLowerCase();
      if (["1", "true", "t", "yes", "y", "نعم", "✓", "ok"].includes(s))
        return tt("options.yes", "نعم");
      if (["0", "false", "f", "no", "n", "لا", "x"].includes(s))
        return tt("options.no", "لا");
      return String(v);
    },
    [t]
  );

  // -----------------------------
  // Filters
  // -----------------------------
  const EMPTY_FILTERS: MosqueFilterState = {
    regionId: null,
    cityId: null,
    centerId: null,
    classificationId: null,
    mosqueTypeId: null,
    hasWomenSection: null,
    // ✅ NEW: shared text filters
    mosqueName: null,
    mosqueNumber: null,
  };

  const [filters, setFilters] = useState<MosqueFilterState>(EMPTY_FILTERS);

  // Used to remount MosqueFilters when switching mode and force visible reset
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  // -----------------------------
  // Location state
  // -----------------------------
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(2);

  const [userLoc, setUserLoc] = useState<LatLng | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const [pickedLoc, setPickedLoc] = useState<LatLng | null>(null);
  const [isPicking, setIsPicking] = useState(false);

  // Current effective location for UI display only
  const effectiveLoc = pickedLoc ?? userLoc ?? null;

  // -----------------------------
  // Data state
  // -----------------------------
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<MosqueMapPoint[]>([]);
  const [selected, setSelected] = useState<MosqueMapPoint | null>(null);
  const [uiError, setUiError] = useState<string>("");

  // -----------------------------
  // Map state
  // -----------------------------
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_ZOOM);

  const mapRef = useRef<google.maps.Map | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback((pos: LatLng, zoom?: number) => {
    if (!mapRef.current) return;
    mapRef.current.panTo(pos);
    if (typeof zoom === "number") mapRef.current.setZoom(zoom);
  }, []);

  // -----------------------------
  // Helpers to switch search mode cleanly
  // -----------------------------
  // Clear any active location mode when user goes back to region/city mode
  const clearAllLocation = useCallback(() => {
  // Clear location state
  setPickedLoc(null);
  setUserLoc(null);
  setIsPicking(false);
  setGeoError(null);

  // Clear currently shown map results/info that belonged to location mode
  setItems([]);
  setSelected(null);
  setUiError("");
}, []);

  // Clear any active region/city mode when user switches to location mode
  const clearRegionCityMode = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setFiltersResetKey((k) => k + 1);
    setItems([]);
    setSelected(null);
    setUiError("");
  }, []);

  // -----------------------------
  // Search rules
  // -----------------------------
  const canSearchByRegionCity = !!filters.regionId && !!filters.cityId;

  // Convert MosqueFilters boolean to API string
  const womenToApi = (v: boolean | null | undefined) => {
    if (v === true) return "yes";
    if (v === false) return "no";
    return undefined;
  };

  // -----------------------------
  // Core search
  //
  // IMPORTANT:
  // We accept both optional filters and optional location override.
  // This is the key fix for location mode:
  // when user picks/gets location, we search using that exact location immediately,
  // without waiting for React state to settle.
  // -----------------------------
  const doSearch = useCallback(
    async (overrideFilters?: MosqueFilterState, overrideLoc?: LatLng | null) => {
      const f = overrideFilters ?? filters;
      const loc = overrideLoc ?? effectiveLoc;

      setUiError("");
      setSelected(null);

      const hasRegionCity = !!f.regionId && !!f.cityId;
      const hasLocation = !!loc;

      // Either location OR region+city must exist
      if (!hasLocation && !hasRegionCity) {
        setItems([]);
        setUiError(
          tt(
            "messages.mustSelectRegionCity",
            "يرجى اختيار المنطقة والمدينة (أو اختيار موقع) ثم الضغط على بحث."
          )
        );
        return;
      }

      setLoading(true);

      try {
        const res = await listMosqueMapPoints({
          regionId: hasLocation ? undefined : f.regionId ?? undefined,
          cityId: hasLocation ? undefined : f.cityId ?? undefined,
          centerId: hasLocation ? undefined : f.centerId ?? undefined,
          classificationId: f.classificationId ?? undefined,
          hasWomenPrayerRoom: womenToApi(f.hasWomenSection),
          // ✅ NEW: optional mosque filters
          mosqueName: f.mosqueName ?? undefined,
          mosqueNumber: f.mosqueNumber ?? undefined,
          userLat: loc?.lat,
          userLng: loc?.lng,
          radiusM: loc ? kmToMeters(radiusKm) : undefined,
          limit: 2000,
        });

        const cleaned = (res ?? []).filter(
          (x: any) => x.latitude != null && x.longitude != null
        );

        setItems(cleaned);

        // Map movement rules
        if (hasLocation && loc) {
          setMapCenter(loc);
          setMapZoom(13);
          panTo(loc, 13);
        } else if (cleaned.length > 0) {
          const p = {
            lat: cleaned[0].latitude as number,
            lng: cleaned[0].longitude as number,
          };
          setMapCenter(p);
          setMapZoom(12);
          panTo(p, 12);
        }
      } catch (e: any) {
        console.error("listMosqueMapPoints failed", e);
        setItems([]);
        setUiError(e?.message || tt("messages.failedToLoad", "فشل تحميل المساجد."));
      } finally {
        setLoading(false);
      }
    },
    [filters, effectiveLoc, radiusKm, panTo, t]
  );

  // -----------------------------
  // Filter apply
  //
  // Region/city mode uses the existing MosqueFilters apply flow.
  // When user applies filters, we clear location mode first.
  // -----------------------------
  const handleApplyFilters = useCallback(
    (f: MosqueFilterState) => {
      // Switching to filter/region-city mode
      clearAllLocation();

      setFilters(f);

      const allNull =
        !f.regionId &&
        !f.cityId &&
        !f.centerId &&
        !f.classificationId &&
        !f.mosqueTypeId &&
        f.hasWomenSection === null&&

        // ✅ NEW: text filters must also be empty
        !f.mosqueName &&
        !f.mosqueNumber;
        
      // If user reset filters and no location exists, just clear UI
      if (allNull) {
        setItems([]);
        setSelected(null);
        setUiError("");
        setGeoError(null);
        return;
      }

      // Search using region/city/filter mode
      void doSearch(f, null);
    },
    [doSearch, clearAllLocation]
  );

  // -----------------------------
  // Map click = pick location
  //
  // FIX:
  // We call doSearch(..., p) immediately using the picked location.
  // -----------------------------
  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {

      clearRegionCityMode();

      if (!isPicking) return;

      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;

      const p = { lat, lng };

      // Switching to location mode
      clearRegionCityMode();

      setPickedLoc(p);
      setUserLoc(null);
      setIsPicking(false);

      setUiError("");
      setGeoError(null);

      setMapCenter(p);
      setMapZoom(13);
      panTo(p, 13);

      // ✅ Immediate API call with explicit location override
      void doSearch(EMPTY_FILTERS, p);
    },
    [isPicking, panTo, clearRegionCityMode, doSearch]
  );

  // -----------------------------
  // Current location
  //
  // FIX:
  // We call doSearch(..., p) immediately using the current location.
  // -----------------------------
  const requestUserLocation = useCallback(() => {
    clearRegionCityMode();
    setGeoError(null);
    setUiError("");
    setGeoLoading(true);

    if (!navigator.geolocation) {
      setGeoLoading(false);
      setGeoError(tt("messages.geoNotSupported", "المتصفح لا يدعم تحديد الموقع."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };

        // Switching to location mode
        clearRegionCityMode();

        setUserLoc(p);
        setPickedLoc(null);

        setGeoLoading(false);

        setMapCenter(p);
        setMapZoom(13);
        panTo(p, 13);

        // ✅ Immediate API call with explicit location override
        void doSearch(EMPTY_FILTERS, p);
      },
      (err) => {
        setGeoLoading(false);
        setGeoError(
          err.message || tt("messages.locationDenied", "تم رفض الوصول إلى الموقع.")
        );
        setUserLoc(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 }
    );
  }, [panTo, t, clearRegionCityMode, doSearch]);

  const togglePickLocation = useCallback(() => {
    setUiError("");
    setGeoError(null);
    setIsPicking((v) => !v);
  }, []);

  const clearSelectedLocation = useCallback(() => {
    setPickedLoc(null);
    setUserLoc(null);
    setIsPicking(false);
    setItems([]);
    setSelected(null);
    setUiError("");
    setGeoError(null);
  }, []);

  const openInGoogleMaps = useCallback(() => {
    if (!selected?.latitude || !selected?.longitude) return;

    const dest = { lat: selected.latitude, lng: selected.longitude };
    const url = effectiveLoc
      ? buildGoogleMapsDirectionsUrl(effectiveLoc, dest)
      : buildGoogleMapsPinUrl(dest);

    window.open(url, "_blank", "noopener,noreferrer");
  }, [selected, effectiveLoc]);

  // -----------------------------
  // Layout tokens
  // -----------------------------
  const pageWrap = "max-w-7xl mx-auto px-4 md:px-6 py-16";

  const locationCard =
    "relative w-full bg-white rounded-[32px] border border-[#E5E7EB] shadow-[0_12px_40px_rgba(16,24,40,0.08)] px-6 md:px-8 py-6";

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className={pageWrap}>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: "الرئيسية", to: "/" },
            { label: "بحث المساجد", to: "/mosqque/mosque-search" },
            { label: tt("title", "خريطة المساجد") },
          ]}
        />

        <MosqueFilters
          key={`mosque-map-filters-${filtersResetKey}`}
          initial={filters}
          onApply={handleApplyFilters}
        />

        <div className={locationCard}>
          <div className="text-[15px] font-semibold text-[#111827] text-center w-full">
            {tt("labels.radius", "نطاق البحث")}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <button
                type="button"
                onClick={requestUserLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={geoLoading}
              >
                {geoLoading
                  ? tt("buttons.gettingLocation", "جاري تحديد الموقع...")
                  : tt("buttons.useMyLocation", "استخدم موقعي")}
              </button>

              <button
                type="button"
                onClick={togglePickLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
              >
                {isPicking
                  ? tt("buttons.cancelPick", "إلغاء الاختيار")
                  : tt("buttons.pickLocation", "اختيار موقع على الخريطة")}
              </button>

              <button
                type="button"
                onClick={clearSelectedLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={!pickedLoc && !userLoc}
              >
                {tt("buttons.clearPicked", "مسح الموقع المختار")}
              </button>
            </div>

            <div className="min-w-[240px] w-full md:w-[280px]">
              <select
                className="modern-filter-select font-arabic"
                style={{
                  fontFamily: "var(--font-family-arabic)",
                  fontWeight: "var(--font-weight-normal)",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  appearance: "none",
                  backgroundImage: "none",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "unset",
                  backgroundSize: "unset",
                  backgroundOrigin: "unset",
                  backgroundClip: "unset",
                  paddingInlineEnd: "36px",
                  paddingInlineStart: "36px",
                }}
                value={radiusKm}
                onChange={(e) => {
                  const nextRadius = Number(e.target.value) as RadiusKm;
                  setRadiusKm(nextRadius);

                  // Re-run location search immediately when radius changes
                  if (effectiveLoc) {
                    void doSearch(EMPTY_FILTERS, effectiveLoc);
                  }
                }}
                disabled={!effectiveLoc}
              >
                {RADIUS_KM_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k} km
                  </option>
                ))}
              </select>
            </div>
          </div>

          {geoError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {geoError}
            </div>
          )}

          {uiError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {uiError}
            </div>
          )}

          {isPicking && (
            <div className="mt-4 rounded-[18px] border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-3 text-sm text-[#1D4ED8]">
              {tt("messages.clickMapToPick", "اضغط على الخريطة لاختيار موقع.")}
            </div>
          )}

          <div className="mt-5 text-[15px] text-[#6B7280] font-semibold text-center">
            {effectiveLoc ? (
              <span>
                {tt("status.locationMode", "وضع الموقع")}:{" "}
                <b className="text-[#111827]">
                  {effectiveLoc.lat.toFixed(5)}, {effectiveLoc.lng.toFixed(5)}
                </b>{" "}
                | {tt("labels.radius", "نطاق البحث")}:{" "}
                <b className="text-[#111827]">{radiusKm} km</b>
              </span>
            ) : (
              <span>
                {tt("status.regionCityMode", "وضع المنطقة/المدينة")}:{" "}
                <b className="text-[#111827]">
                  {canSearchByRegionCity
                    ? tt("status.ready", "تلقائي")
                    : tt("status.selectRegionCity", "اختر المنطقة + المدينة")}
                </b>
              </span>
            )}
            <span className="mx-2">|</span>
            {tt("status.points", " عدد النتائج")}:{" "}
            <b className="text-[#111827]">{items.length}</b>
          </div>
        </div>

        <div
          className="dga-card"
          style={{ borderRadius: "32px", overflow: "hidden" }}
        >
          <div style={{ height: "70vh", width: "100%" }}>
            {!GOOGLE_MAPS_API_KEY ? (
              <div className="p-4 text-sm">
                {tt(
                  "messages.missingApiKey",
                  "مفتاح Google Maps غير موجود في ملف الإعدادات."
                )}
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm">
                Failed to load Google Maps: {String(loadError)}
              </div>
            ) : !isLoaded ? (
              <div className="p-4 text-sm">
                {tt("messages.loadingMap", "جاري تحميل الخريطة...")}
              </div>
            ) : (
              <GoogleMap
                onLoad={onMapLoad}
                onClick={onMapClick}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  clickableIcons: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  streetViewControl: false,
                }}
              >
                {userLoc && (
                  <MarkerF
                    position={userLoc}
                    title={tt("infoWindow.yourLocation", "موقعك")}
                    onClick={() => setSelected(null)}
                  />
                )}

                {pickedLoc && (
                  <MarkerF
                    position={pickedLoc}
                    title={tt("infoWindow.selectedLocation", "الموقع المختار")}
                    onClick={() => setSelected(null)}
                  />
                )}

                <MarkerClustererF>
                  {(clusterer) => (
                    <>
                      {items.map((m) => {
                        if (m.latitude == null || m.longitude == null) return null;
                        return (
                          <MarkerF
                            key={m.id}
                            position={{ lat: m.latitude, lng: m.longitude }}
                            clusterer={clusterer}
                            onClick={() => setSelected(m)}
                            title={m.mosqueName}
                            icon={mosqueIcon}
                          />
                        );
                      })}
                    </>
                  )}
                </MarkerClustererF>

                {selected && selected.latitude != null && selected.longitude != null && (
                  <InfoWindowF
                    position={{ lat: selected.latitude, lng: selected.longitude }}
                    onCloseClick={() => setSelected(null)}
                  >
                    <div style={{ maxWidth: 280 }}>
                      <div className="font-semibold">{selected.mosqueName}</div>
                      <div className="text-sm opacity-80">
                        {selected.mosqueClassification}
                      </div>

                      <div className="mt-2 text-sm">
                        <div>
                          <b>{tt("infoWindow.women", "مصلى النساء")}:</b>{" "}
                          {formatWomen(selected.hasWomenPrayerRoom)}
                        </div>
                        <div>
                          <b>{tt("infoWindow.capacity", "السعة")}:</b>{" "}
                          {selected.capacity ?? "—"}
                        </div>
                        {selected.distanceM != null && (
                          <div>
                            <b>{tt("infoWindow.distance", "المسافة")}:</b>{" "}
                            {(selected.distanceM / 1000).toFixed(2)} km
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/mosques/${selected.id}`, {
                            state: { fromMap: true, source: "mosque-map" },
                          });
                        }}
                        className="mt-3 w-full rounded-xl bg-sa-600 px-6 py-3 text-base font-semibold text-white hover:bg-sa-700 transition-all duration-300"
                      >
                        عرض التفاصيل
                      </button>

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="dga-btn dga-btn-secondary"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={openInGoogleMaps}
                          title={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                          aria-label={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M12 22s7-5.6 7-13a7 7 0 1 0-14 0c0 7.4 7 13 7 13z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="12"
                              cy="9"
                              r="2.5"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        </button>
                      </div>

                      {!effectiveLoc && (
                        <div className="mt-2 text-xs opacity-70">
                          {tt(
                            "infoWindow.tipForDirections",
                            'معلومة: استخدم "استخدم موقعي" أو "اختيار موقع" لفتح الاتجاهات تلقائيًا.'
                          )}
                        </div>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}